<template>
  <div id="app">
    <header class="site-header">
      <router-link class="brand-link" to="/" aria-label="Workflow Shortcuts homepage">
        <img class="brand-logo" :src="logo" alt="Workflow Shortcuts">
      </router-link>
      <nav class="site-nav" aria-label="Primary navigation">
        <router-link
          to="/"
          :class="{ 'nav-link-active': $route.name === 'home' }"
        >
          Documents
        </router-link> |
        <router-link to="/boards">Boards</router-link> |
        <router-link to="/about">About</router-link>
      </nav>
      <div v-if="authReady && googleAuthEnabled" class="auth-shell">
        <div v-if="currentUser" key="auth-user" class="auth-user">
          <span>{{ currentUser.name || currentUser.email }}</span>
          <button type="button" @click="handleSignOut">Sign out</button>
        </div>
        <div v-else key="google-sign-in" ref="googleSignIn"></div>
      </div>
    </header>

    <div v-if="authError" class="auth-error" role="status">
      {{ authError }}
    </div>

    <main class="container app-main">
      <router-view/>
    </main>

    <footer class="site-footer">
      <span>&copy; {{ currentYear }} Workflow Shortcuts</span>
    </footer>
  </div>
</template>

<script>
import logo from './assets/workflowshortcuts-logo.svg';
import { getErrorMessage } from './services/resourceService';
import {
  getAuthConfig,
  getCurrentUser,
  renderGoogleButton,
  signInWithGoogleCredential,
  signOut,
} from './services/authService';

export default {
  name: 'App',
  data() {
    return {
      authReady: false,
      currentYear: new Date().getFullYear(),
      currentUser: null,
      authError: '',
      googleAuthEnabled: false,
      googleClientId: '',
      logo,
    };
  },
  async mounted() {
    await this.initializeAuth();
  },
  methods: {
    async initializeAuth() {
      try {
        this.authError = '';
        const config = await getAuthConfig();
        this.googleAuthEnabled = config.googleAuthEnabled === true;
        this.googleClientId = config.googleClientId || '';
        this.currentUser = await getCurrentUser();
      } catch (error) {
        this.googleAuthEnabled = false;
      } finally {
        this.authReady = true;
        this.$nextTick(this.renderSignInButton);
      }
    },

    async renderSignInButton() {
      if (!this.googleAuthEnabled || this.currentUser) {
        return;
      }

      await renderGoogleButton(this.$refs.googleSignIn, this.googleClientId, this.handleCredential);
    },

    async handleCredential(response) {
      try {
        this.authError = '';
        this.currentUser = await signInWithGoogleCredential(response.credential);
        window.dispatchEvent(new CustomEvent('workflowshortcuts-auth-changed'));
      } catch (error) {
        this.currentUser = null;
        this.authError = getErrorMessage(error);
        this.$nextTick(this.renderSignInButton);
      }
    },

    async handleSignOut() {
      await signOut();
      this.currentUser = null;
      this.authError = '';
      this.$nextTick(this.renderSignInButton);
      window.dispatchEvent(new CustomEvent('workflowshortcuts-auth-changed'));
    },
  },
}
</script>

<style>
#app {
  display: flex;
  flex-direction: column;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
}

.site-header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e8edf0;
  display: flex;
  gap: 32px;
  justify-content: center;
  min-height: 70px;
  padding: 14px 24px;
  position: sticky;
  top: 0;
  z-index: 20;
}

.brand-link {
  align-items: center;
  display: inline-flex;
  left: 24px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.brand-link:focus {
  border-radius: 6px;
  outline: 3px solid rgba(66, 185, 131, 0.22);
  outline-offset: 4px;
}

.brand-logo {
  display: block;
  height: 42px;
  width: 195px;
}

.site-nav {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin: 0 auto;
  text-align: center;
}

.site-nav a {
  font-weight: bold;
  color: #2c3e50;
}
.site-nav a.router-link-exact-active,
.site-nav a.nav-link-active {
  color: #42b983;
}

.auth-shell {
  align-items: center;
  display: flex;
  min-height: 40px;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
}

.auth-user {
  align-items: center;
  display: flex;
  gap: 10px;
}

.auth-user span {
  font-size: 13px;
  font-weight: 700;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-user button {
  background: #ffffff;
  border: 1px solid #42b983;
  border-radius: 4px;
  color: #25724f;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 10px;
}

.auth-user button:hover,
.auth-user button:focus {
  background: #42b983;
  color: #ffffff;
}

.app-main {
  flex: 1 0 auto;
  margin-top: 32px;
  padding-bottom: 100px;
  text-align: center;
}

.auth-error {
  background: #fff8f6;
  border-bottom: 1px solid #f0c9bf;
  color: #8a2f1d;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  padding: 10px 16px;
  text-align: center;
}

.app-notice {
  border: 1px solid #cfdad5;
  border-left: 4px solid #2f9e74;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(44, 62, 80, 0.05);
  color: #263b35;
  font-size: 15px;
  line-height: 1.45;
  margin: 0 0 16px;
  padding: 12px 14px;
  text-align: left;
}

.app-notice-error {
  background: #f7fbf8;
}

.site-footer {
  align-items: center;
  background: #42b983;
  color: #ffffff;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  justify-content: center;
  line-height: 1.2;
  margin-top: auto;
  min-height: 34px;
  padding: 8px 16px;
  text-align: center;
}

#app .shortcuts-container {
  flex-direction: column;
}

@media (max-width: 575px) {
  .site-header {
    align-items: center;
    min-height: 96px;
    padding: 58px 16px 12px;
  }

  .brand-link {
    left: 16px;
    top: 12px;
    transform: none;
  }

  .brand-logo {
    height: 36px;
    width: 167px;
  }

  .site-nav {
    justify-content: center;
    width: 100%;
  }

  .auth-shell {
    margin-top: 12px;
    position: static;
    transform: none;
  }

  .app-main {
    margin-top: 24px;
  }
}
</style>
